import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import placementTestService, { CourseRecommendation, RecommendationsResponse } from '../services/placementTestService';
import enrollmentService from '../services/enrollmentService';

interface Props {
    sessionId: string;
    primaryLevel: string;
    t: (key: string, opts?: Record<string, unknown>) => string;
    locale: string;
}

const RecommendedCoursesSection: React.FC<Props> = ({
    sessionId, primaryLevel, t, locale,
}) => {
    const navigate = useNavigate();
    const [recommendations, setRecommendations] = useState<CourseRecommendation[]>([]);
    const [weakKps, setWeakKps] = useState<RecommendationsResponse['weakKnowledgePoints']>([]);
    const [loading, setLoading] = useState(true);
    const [enrollingIds, setEnrollingIds] = useState<Set<string>>(new Set());
    const [enrolledIds, setEnrolledIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        Promise.all([
            placementTestService.getRecommendations(sessionId),
            enrollmentService.getMyEnrollments().catch(() => []),
        ])
            .then(([data, enrollments]) => {
                setRecommendations(data.recommendations);
                setWeakKps(data.weakKnowledgePoints || []);
                const enrolledCourseIds = new Set(enrollments.map(e => e.courseId));
                setEnrolledIds(enrolledCourseIds);
            })
            .catch(() => { /* optional section, ignore errors */ })
            .finally(() => setLoading(false));
    }, [sessionId]);

    const handleEnroll = async (courseId: string) => {
        setEnrollingIds(prev => new Set(prev).add(courseId));
        try {
            await enrollmentService.enroll(courseId);
            setEnrolledIds(prev => new Set(prev).add(courseId));
        } catch {
            /* enrollment may fail if already enrolled — ignore */
        } finally {
            setEnrollingIds(prev => {
                const next = new Set(prev);
                next.delete(courseId);
                return next;
            });
        }
    };

    if (loading) {
        return (
            <div className="recommended-section">
                <h3>{t('result.recommendedCourses')}</h3>
                <div className="recommended-loading">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="recommended-card recommended-card--skeleton" />
                    ))}
                </div>
            </div>
        );
    }

    if (recommendations.length === 0) {
        return (
            <div className="recommended-section">
                <h3>{t('result.recommendedCourses')}</h3>
                <p className="recommended-empty">{t('result.noRecommendations')}</p>
                <button
                    className="recommended-browse-btn"
                    onClick={() => navigate(`/courses?level=${primaryLevel}`)}
                >
                    {t('result.browseCourses')}
                </button>
            </div>
        );
    }

    return (
        <div className="recommended-section">
            <h3>{t('result.recommendedCourses')}</h3>
            <p className="recommended-desc">{t('result.recommendedDesc')}</p>

            {/* All weak knowledge points summary */}
            {weakKps.length > 0 && (
                <div className="weak-kps-summary">
                    <span className="weak-kps-label">{t('result.yourWeakPoints')}</span>
                    <div className="weak-kps-list">
                        {weakKps.map((kp, i) => (
                            <span key={i} className="weak-kp-chip">
                                <span className="weak-kp-chip-category">
                                    {t(`result.knowledgeCategory.${kp.category}`)}
                                </span>
                                <span className="weak-kp-chip-desc">
                                    {kp.weaknessDescriptor[locale] || kp.weaknessDescriptor['en'] || kp.term}
                                </span>
                            </span>
                        ))}
                    </div>
                </div>
            )}

            <div className="recommended-grid">
                {recommendations.map(course => {
                    const title = course.titles?.[locale] || course.titles?.['en'] || course.title;
                    const hasWeakKps = course.matchedKnowledgePoints.length > 0;
                    const isEnrolled = enrolledIds.has(course.courseId);
                    const isEnrolling = enrollingIds.has(course.courseId);

                    return (
                        <div key={course.courseId} className="recommended-card">
                            <a
                                className="recommended-card-link"
                                href={`/courses/${course.courseId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <div className="recommended-card-thumb">
                                    {course.thumbnailUrl ? (
                                        <img src={course.thumbnailUrl} alt={title} />
                                    ) : (
                                        <div className="recommended-card-thumb-placeholder" />
                                    )}
                                    {course.cefrLevel && (
                                        <span className="recommended-card-level">{course.cefrLevel}</span>
                                    )}
                                </div>
                                <div className="recommended-card-body">
                                    <h4 className="recommended-card-title">{title}</h4>
                                    <span className="recommended-card-creator">{course.creatorName}</span>
                                    {hasWeakKps && (
                                        <div className="recommended-card-reason">
                                            <span className="recommended-card-reason-label">
                                                {t('result.coversWeakPoints', { count: course.matchedKnowledgePoints.length })}
                                            </span>
                                            <div className="recommended-card-kp-tags">
                                                {course.matchedKnowledgePoints.map((kp, i) => (
                                                    <span key={i} className="recommended-kp-tag">
                                                        <span className="recommended-kp-category">
                                                            {t(`result.knowledgeCategory.${kp.category}`)}
                                                        </span>
                                                        <span className="recommended-kp-desc">
                                                            {kp.weaknessDescriptor[locale] || kp.weaknessDescriptor['en'] || ''}
                                                        </span>
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {!hasWeakKps && course.topicMatch && (
                                        <div className="recommended-card-reason">
                                            <span className="recommended-card-topic-match">
                                                {t('result.topicMatch')}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </a>
                            <div className="recommended-card-actions">
                                <button
                                    className={`recommended-enroll-btn ${isEnrolled ? 'recommended-enroll-btn--enrolled' : ''}`}
                                    disabled={isEnrolling || isEnrolled}
                                    onClick={() => handleEnroll(course.courseId)}
                                >
                                    {isEnrolling
                                        ? t('result.enrolling')
                                        : isEnrolled
                                            ? t('result.enrolled')
                                            : t('result.enrollCourse')}
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default RecommendedCoursesSection;
